import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import {Pagination} from '@primer/react'
import {SkeletonText} from '@primer/react/experimental'
import type React from 'react'

import {AIResourceResults, type AIResourceResult} from './AIResourceResults'
import styles from './AIResourceSearchPage.module.css'
import {ResourceTypeFilter, type ResourceTypeFilterProps} from './ResourceTypeFilter'
import {SearchCategoryNav, type AIResourceSearchCategory} from './SearchCategoryNav'
import {SearchResultsHeader} from './SearchResultsHeader'
import NoResults from '../NoResults/NoResults'
import SearchError from '../SearchError'
import type {SearchKind, SearchKindCount} from '../../hooks/use-search-result-counts'
import type {QueryError} from '../../types/blackbird-types'

export type AIResourceSearchPageState =
  | {
      status: 'success'
      results: readonly AIResourceResult[]
      totalCount?: number
      currentPage?: number
      pageCount?: number
      getPageHref?: (page: number) => string
    }
  | {status: 'loading'}
  | {status: 'empty'}
  | {status: 'error'; errors: readonly QueryError[]}

export type AIResourceSearchPageProps = {
  categories: readonly AIResourceSearchCategory[]
  elapsedMilliseconds?: number
  filters: ResourceTypeFilterProps
  isLoadingCounters: boolean
  onPageChange?: (event: React.MouseEvent, page: number) => void
  query: string
  resultKind: SearchKind
  searchKinds: SearchKindCount[]
  state: AIResourceSearchPageState
}

export function AIResourceSearchPage({
  categories,
  elapsedMilliseconds,
  filters,
  isLoadingCounters,
  onPageChange,
  query,
  resultKind,
  searchKinds,
  state,
}: AIResourceSearchPageProps) {
  return (
    <FilteredListPageLayout>
      <FilteredListPageLayout.Header>
        <FilteredListPageLayout.PageHeader>
          <FilteredListPageLayout.PageHeader.TitleArea>
            <FilteredListPageLayout.PageHeader.Title as="h1" className="sr-only">
              Search results
            </FilteredListPageLayout.PageHeader.Title>
          </FilteredListPageLayout.PageHeader.TitleArea>
        </FilteredListPageLayout.PageHeader>
      </FilteredListPageLayout.Header>

      <FilteredListPageLayout.Sidebar
        aria-label="Search filters"
        dialogTitle="Search filters"
        expandedWidth="medium"
        scrollable
      >
        <SearchCategoryNav categories={categories} />
        <ResourceTypeFilter {...filters} />
      </FilteredListPageLayout.Sidebar>

      <FilteredListPageLayout.Content as="div">
        <SearchResultsHeader
          elapsedMilliseconds={elapsedMilliseconds}
          errorMessage={state.status === 'error' ? state.errors[0]?.message : undefined}
          query={query}
          status={state.status}
          totalCount={state.status === 'success' ? state.totalCount : undefined}
        />
        {state.status === 'error' ? (
          <div className={styles.Notices}>
            {state.errors.slice(0, 2).map((error, index) => (
              // eslint-disable-next-line @eslint-react/no-array-index-key -- matches the shared Search error list
              <SearchError error={error} index={index} key={index} />
            ))}
          </div>
        ) : null}
        <FilteredListPageLayout.Results aria-label="AI resource results">
          <ResultsContent
            isLoadingCounters={isLoadingCounters}
            onPageChange={onPageChange}
            resultKind={resultKind}
            searchKinds={searchKinds}
            state={state}
          />
        </FilteredListPageLayout.Results>
      </FilteredListPageLayout.Content>
    </FilteredListPageLayout>
  )
}

function ResultsContent({
  isLoadingCounters,
  onPageChange,
  resultKind,
  searchKinds,
  state,
}: {
  isLoadingCounters: boolean
  onPageChange?: (event: React.MouseEvent, page: number) => void
  resultKind: SearchKind
  searchKinds: SearchKindCount[]
  state: AIResourceSearchPageState
}) {
  switch (state.status) {
    case 'success':
      return <SuccessResults onPageChange={onPageChange} state={state} />
    case 'loading':
      return <LoadingResults />
    case 'empty':
    case 'error':
      return <NoResults resultKind={resultKind} searchKinds={searchKinds} isLoadingCounters={isLoadingCounters} />
  }
}

function SuccessResults({
  onPageChange,
  state,
}: {
  onPageChange?: (event: React.MouseEvent, page: number) => void
  state: Extract<AIResourceSearchPageState, {status: 'success'}>
}) {
  const {currentPage, getPageHref, pageCount} = state
  const showPagination = currentPage !== undefined && pageCount !== undefined && pageCount > 1 && getPageHref

  return (
    <>
      <AIResourceResults results={state.results} />
      {showPagination && (
        <div className={styles.Pagination}>
          <Pagination
            currentPage={currentPage}
            hrefBuilder={getPageHref}
            onPageChange={onPageChange}
            pageCount={pageCount}
          />
        </div>
      )}
    </>
  )
}

function LoadingResults() {
  return (
    <div className={styles.LoadingList} aria-hidden="true">
      <LoadingResultRow />
      <LoadingResultRow />
      <LoadingResultRow />
    </div>
  )
}

function LoadingResultRow() {
  return (
    <div className={styles.LoadingRow}>
      <SkeletonText size="titleMedium" />
      <SkeletonText size="bodyMedium" lines={2} />
    </div>
  )
}
